// The legacy-record editor's figures (time, equipment, crew, cost items) on
// top of SQ-500's facts: the draft shapes the work-item editor keeps, how
// they turn into the save request, and what still reads as "from before the
// current design" on a record.
import {
  LibraryCrewRowDto, LibraryEquipmentRowDto, LibraryEstimateStateDto, LibraryOperationFiguresDto, LibraryWorkItemStateDto,
  SaveLibraryEquipmentDto, SaveLibraryOperationDto,
} from '../../../../shared/dtos/comparable-library.dto';
import {MissingFact} from '../missingFacts';

export interface OperationFiguresDraft {
  /** What the crew spent on the operation; null = not recorded. Estimated time is redistributed at the job level, not typed here. */
  actualSeconds: number | null;
  notes: string;
}

export interface EquipmentRowDraft {
  equipmentId: number;
  durationMinutes: number;
  actualDurationMinutes: number | null;
  /** The locked rate; one of the row's own or the catalog's today. */
  hourlyRate: number | null;
  minimumCharge: number | null;
}

export interface FiguresDraft {
  operations: Record<number, OperationFiguresDraft>;
  equipment: EquipmentRowDraft[];
}

/** The saved figures the editor starts from. */
export function figuresOf(item: LibraryWorkItemStateDto | undefined): FiguresDraft {
  const operations: Record<number, OperationFiguresDraft> = {};
  item?.operations.forEach(op => {
    operations[op.operationId] = draftOfOperation(op.figures);
  });
  return {operations, equipment: (item?.equipment ?? []).map(draftOfEquipment)};
}

export const draftOfOperation = (figures?: LibraryOperationFiguresDto | null): OperationFiguresDraft => ({
  actualSeconds: figures?.actualSeconds ?? null,
  notes: figures?.notes ?? '',
});

export const draftOfEquipment = (row: LibraryEquipmentRowDto): EquipmentRowDraft => ({
  equipmentId: row.equipmentId,
  durationMinutes: row.durationMinutes,
  actualDurationMinutes: row.actualDurationMinutes ?? null,
  hourlyRate: row.hourlyRate,
  minimumCharge: row.minimumCharge,
});

export const sameFigures = (a: FiguresDraft, b: FiguresDraft) => JSON.stringify(a) === JSON.stringify(b);

/** The operation figures that changed, as the request's optional fields (facts travel beside them). */
export function operationFiguresPatch(
  operationId: number, saved: OperationFiguresDraft | undefined, draft: OperationFiguresDraft | undefined,
): Partial<SaveLibraryOperationDto> {
  if (!draft) return {};
  const patch: Partial<SaveLibraryOperationDto> = {};
  if (draft.actualSeconds !== (saved?.actualSeconds ?? null)) {
    if (draft.actualSeconds === null) patch.clearActuals = true;
    else patch.actualSeconds = draft.actualSeconds;
  }
  if (draft.notes.trim() !== (saved?.notes ?? '').trim()) patch.notes = draft.notes.trim();
  return patch;
}

export const equipmentRequest = (rows: EquipmentRowDraft[]): SaveLibraryEquipmentDto[] => rows.map(row => ({
  equipmentId: row.equipmentId,
  durationMinutes: row.durationMinutes,
  actualDurationMinutes: row.actualDurationMinutes,
  hourlyRate: row.hourlyRate,
  minimumCharge: row.minimumCharge,
}));

/** Origins that mean "a split of the old job timer", not time anyone recorded. */
export const DERIVED_ORIGINS = ['derived_job_length', 'derived'];

/**
 * What a work item still carries from before the current design, in the
 * order the editor shows them. Like missingFacts: a prompt, never a block.
 */
export function missingFigures(item: LibraryWorkItemStateDto | undefined, draft?: FiguresDraft): MissingFact[] {
  if (!item) return [];
  const missing: MissingFact[] = [];
  item.operations.forEach(op => {
    const figures = draft?.operations[op.operationId] ?? draftOfOperation(op.figures);
    if (figures.actualSeconds === null) {
      missing.push({label: `${op.operationType}: actual time`, field: `figure-actual-${op.operationId}`, operationType: op.operationType});
    } else if (DERIVED_ORIGINS.includes(op.figures?.actualsOrigin ?? '') && figures.actualSeconds === (op.figures?.actualSeconds ?? null)) {
      missing.push({label: `${op.operationType}: actual time is the old job timer's split`, field: `figure-actual-${op.operationId}`, operationType: op.operationType});
    }
  });
  const equipment = draft?.equipment ?? (item.equipment ?? []).map(draftOfEquipment);
  equipment.forEach(row => {
    const saved = item.equipment?.find(e => e.equipmentId === row.equipmentId);
    const name = saved?.nickname ?? 'Equipment';
    // A machine whose billed time was an old method's guess, or that bills none, has no recorded time until its actual time is.
    if (row.actualDurationMinutes === null && (row.durationMinutes <= 0 || saved?.origin === 'legacy_methods')) {
      missing.push({label: `${name}: actual time`, field: `figure-equipment-${row.equipmentId}`});
    }
  });
  return missing;
}

/** What the estimate as a whole still lacks: a crew on the job, rates locked by the V41 backfill, a pricing copy. */
export function missingRecordFigures(state: LibraryEstimateStateDto): string[] {
  const record = state.record;
  if (!record) return [];
  const missing: string[] = [];
  const onJob = record.crew.filter(member => member.onJob);
  if (record.workOrderId && onJob.length === 0) missing.push('No crew on the job');
  if (onJob.some(member => member.hourlyRateSource === 'legacy_company_rate')) missing.push('Crew rates are the company rate the V41 backfill assumed');
  if (onJob.length > 0 && onJob.every(member => member.secondsWorked <= 0)) missing.push('No crew time');
  if (record.laborRatePerHour === null && record.laborCost > 0) missing.push('No crew rate, so the prices stand for no time yet');
  if (record.actualsFollowEstimate) missing.push('Actual time is the old job timer split by estimate; it follows the estimate until placed by hand');
  if (!record.pricing.applied) missing.push('No pricing rules copy (prices with none)');
  return missing;
}

export const crewSeconds = (crew: LibraryCrewRowDto[]) => crew.filter(m => m.onJob).reduce((sum, m) => sum + m.secondsWorked, 0);

/**
 * One row of the time distribution takes `wanted` minutes and the others
 * give or take the difference evenly, so the pool stays placed: whole
 * minutes, the leftover going one at a time to the first rows; a row never
 * goes below nothing, its shortfall moving on to the rows that still have
 * time; the edited row can take at most the whole pool. Rows are returned
 * in their order, untouched when there is nothing to balance against.
 */
export function rebalanceMinutes<T extends {operationId: number; minutes: number}>(rows: T[], operationId: number, wanted: number): T[] {
  const edited = rows.find(row => row.operationId === operationId);
  if (!edited) return rows;
  const target = Math.max(0, Math.floor(wanted));
  if (rows.length < 2) return rows.map(row => row.operationId === operationId ? {...row, minutes: target} : row);
  const others = rows.filter(row => row.operationId !== operationId).map(row => ({...row}));
  const available = others.reduce((sum, row) => sum + row.minutes, 0);
  const next = Math.min(target, edited.minutes + available);
  let delta = edited.minutes - next; // positive: the others gain; negative: they give
  for (let guard = 0; delta !== 0 && guard < 100; guard++) {
    const giving = delta < 0;
    const open = others.filter(row => !giving || row.minutes > 0);
    if (open.length === 0) break;
    const share = Math.trunc(delta / open.length);
    let leftover = delta - share * open.length;
    let moved = 0;
    for (const row of open) {
      let give = share;
      if (leftover !== 0) {
        give += Math.sign(leftover);
        leftover -= Math.sign(leftover);
      }
      if (row.minutes + give < 0) give = -row.minutes;
      row.minutes += give;
      moved += give;
    }
    if (moved === 0) break;
    delta -= moved;
  }
  return rows.map(row => row.operationId === operationId ? {...row, minutes: next} : others.find(o => o.operationId === row.operationId) ?? row);
}
